import { useMemo, useState } from 'react';
import { Alert, Box, Checkbox, Collapse, FormControlLabel, IconButton, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BudgetSummary from '../../components/Budget/BudgetSummary';
import ForecastVolumeGrid, { FORECAST_EDIT_KIND } from '../../components/Budget/ForecastVolumeGrid';
import type { HandlingView } from '../../components/Budget/ForecastVolumeGrid';
import OnTopCostsGrid from '../../components/Budget/OnTopCostsGrid';
import MonthWindowNav from '../../components/Budget/MonthWindowNav';
import BudgetMonthRangeFilter from '../../components/Budget/BudgetMonthRangeFilter';
import LoadError from '../../components/Budget/LoadError';
import OverviewSaveBar from '../../components/Budget/OverviewSaveBar';
import HideInactiveFilter, { useHideInactive } from '../../components/Budget/HideInactiveFilter';
import HidePastMonthsFilter, { useHidePastMonths } from '../../components/Budget/HidePastMonthsFilter';
import HintIcon from '../../components/Budget/HintIcon';
import { useOverviewMonths } from '../../components/Budget/useOverviewMonths';
import { useBudgetPeriodMonths } from '../../components/Budget/useBudgetPeriodMonths';
import { formatMonthRange } from '../../components/Budget/helpers';
import UnsavedEditsPrompt from '../../components/Budget/UnsavedEditsPrompt';
import InlineViewToggle from '../../components/UI/InlineViewToggle';
import { useBillingConfigs, useRefreshSourceData } from '../../hooks/api/budgetBillingConfig';
import { useOnTopCosts } from '../../hooks/api/budgetOnTopCost';
import { usePendingCellEdits } from '../../hooks/usePendingCellEdits';
import { useBudgetContext } from '../../store/Budget/BudgetContext';

const BudgetOverview = () => {
    const { t } = useTranslation('budget');
    // CALL-752/756: the extrapolation toggle lives in BudgetContext so the export (handleDownload)
    // shares it — the downloaded file reflects exactly what the grid shows.
    const { selectedVersionId, selectedVersion, isReadOnly, isSnapshot, showExtrapolation, setShowExtrapolation } =
        useBudgetContext();
    const [billingConfigs, isBillingConfigsLoading, billingConfigsState] = useBillingConfigs(selectedVersionId);
    // CALL-816: the months the budget is funded for — named on screen because the row status and its
    // acknowledgement are both judged over them.
    const periodMonths = useBudgetPeriodMonths(selectedVersionId);
    const periodLabel = periodMonths.length
        ? formatMonthRange(periodMonths[0], periodMonths[periodMonths.length - 1])
        : undefined;
    const [onTopCosts, isOnTopCostsLoading] = useOnTopCosts(selectedVersionId);
    const [handlingView, setHandlingView] = useState<HandlingView>('values');
    // CALL-778: presentation-only. Shared with Prices & ABT through one storage key rather than through
    // BudgetContext — the two pages are never mounted together, so a context value would buy nothing and
    // would re-render the version selector on every click. Sticky per browser, on by default.
    const [hideInactive, setHideInactive] = useHideInactive();
    // CALL-781: spans all three sections below, so it sits in the page-level toolbar. Shared with
    // Prices & ABT through one storage key, like the visibility filter above.
    const [hidePastMonths, setHidePastMonths] = useHidePastMonths();
    // CALL-815: the very list the three grids build their month columns from. The page reads it for ONE
    // thing — to notice when the filters have left no columns at all (see the hint below). Read here rather
    // than per grid because the three call the same hook with the same argument, so the condition is
    // identical in all three and one hint at page level says it once.
    const { visibleMonths } = useOverviewMonths(hidePastMonths);
    const { mutate: refreshSourceData, isLoading: isRefreshing } = useRefreshSourceData(selectedVersionId);
    // CALL-779: ONE pending set for the page's three grids, and one Save at the bottom. They write three
    // different resources, so the bar fires a request per resource that has edits (see OverviewSaveBar).
    //
    // Scoped to the VERSION: switching version only rewrites ?versionId= on the same route, so the page
    // does not remount — an unscoped store painted version A's unsaved figures onto B's rows and Save
    // wrote them into B. Each version keeps its own edits, so switching back finds them intact.
    const pending = usePendingCellEdits(selectedVersionId);

    // CALL-820: the orphan alert's list toggle. null = automatic (open while the list is short,
    // collapsed once it would dominate the page); a click pins the user's choice for the session.
    const [orphansListOpen, setOrphansListOpen] = useState<boolean | null>(null);

    // The configs this page holds UNSAVED forecast edits for. Scoped to the forecast kind on purpose: the
    // store is shared with the Ist-Budget row and the on-top grid, whose row ids are independent
    // sequences, so an unscoped read would match an unrelated on-top cost that happens to share an id.
    const editedConfigIds = useMemo(
        () => new Set(pending.edits.filter((edit) => edit.kind === FORECAST_EDIT_KIND).map((edit) => edit.rowId)),
        [pending.edits]
    );

    // CALL-778: hidden rows are dropped from the DETAIL GRID ONLY, and only here — BudgetSummary reads
    // useBillingConfigs itself, and the server's summary/snapshot/export paths all go through
    // loadEnrichedBillingConfigs, so totals and exported data keep every row without any further work.
    // Filtering the hook (or the endpoint) instead would silently change the totals.
    //
    // A row with unsaved edits is never hidden. The pending store is keyed by config id and deliberately
    // outlives the filter — dropping those edits would throw away figures the user typed, over a DISPLAY
    // setting — so without this exception the save bar could count, and Save could post, a row with no
    // cell on screen; worse, a blocking validation issue on it would disable Save with nothing to fix
    // (the Overview's bar has no per-row discard, unlike ConfigSaveBar's onDiscardIssue).
    const visibleConfigs = useMemo(
        () =>
            hideInactive
                ? billingConfigs.filter((config) => !config.isHidden || editedConfigIds.has(config.id))
                : billingConfigs,
        [billingConfigs, hideInactive, editedConfigIds]
    );

    // The prompt is rendered in EVERY branch, including these two: pending edits belong to the versions
    // they were made under, so deselecting the version (or a failed reload) must not leave the router
    // blocked by a guard whose dialog is not on screen.
    if (!selectedVersionId) {
        return (
            <>
                <UnsavedEditsPrompt isDirty={pending.hasEdits} onCurrentView={pending.isDirty} />
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    <span>{t('summary.selectVersionHint')}</span>
                </Typography>
            </>
        );
    }

    if (billingConfigsState.isError) {
        return (
            <>
                <UnsavedEditsPrompt isDirty={pending.hasEdits} onCurrentView={pending.isDirty} />
                <LoadError error={billingConfigsState.error} onRetry={billingConfigsState.refetch} />
            </>
        );
    }

    const hasBillingConfigs = billingConfigs.length > 0;
    const showEmptyHint = !isBillingConfigsLoading && !hasBillingConfigs && !isReadOnly;

    // CALL-820: rows whose working unit has no active strategic volume left — reload can never fill
    // them, so they are named up front rather than discovered row by row. Follows the visibility
    // filter: a hidden orphan is an accepted one.
    const orphanedRowLabels = visibleConfigs
        .filter((config) => config.hasSourceVolume === false)
        .map((config) =>
            [[config.partnerName, config.locationName].filter(Boolean).join(' - '), config.workingUnitName]
                .filter(Boolean)
                .join(' — ')
        );
    const showOrphanList = orphansListOpen ?? orphanedRowLabels.length <= 3;

    // CALL-758: deep-link to the version's bucket (the config page filters by bucket, not version).
    // Omit the query param if the bucket id isn't available so we never emit ?bucketId=undefined,
    // which would fail backend validation.
    const billingConfigLink =
        selectedVersion?.budgetBucketId != null
            ? `/budget/billing-config?bucketId=${selectedVersion.budgetBucketId}`
            : '/budget/billing-config';

    return (
        <>
            {showEmptyHint && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <span>{t('hint.noBillingConfigsOverview')}</span>
                    </Typography>
                    {/* CALL-758: the config page filters by BUCKET, not version — so deep-link to the
                        version's bucket (there is no version selector there). */}
                    <RouterLink to={billingConfigLink}>
                        <span>{t('action.goToBillingConfig')}</span>
                    </RouterLink>
                </Alert>
            )}
            {/* CALL-820: visible without pressing anything — the row icons say the same per row, but a
                reader scanning totals needs the list, not a hunt for LinkOff icons. */}
            {orphanedRowLabels.length > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <IconButton
                            size="small"
                            color="inherit"
                            aria-label={t('action.toggleDetails')}
                            onClick={() => setOrphansListOpen(!showOrphanList)}
                        >
                            {showOrphanList ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    }
                >
                    <Typography variant="body2">
                        <span>{t('hint.orphanedRowsAlert', { count: orphanedRowLabels.length })}</span>
                    </Typography>
                    {/* unmountOnExit, so a collapsed list is really gone — jsdom and screen readers
                        included — rather than sitting at height 0. One label per line: the joined
                        one-paragraph version stopped being scannable at half a dozen rows. */}
                    <Collapse in={showOrphanList} unmountOnExit>
                        <Box component="ul" sx={{ m: 0, pl: 2.5, pt: 0.5 }}>
                            {orphanedRowLabels.map((label) => (
                                <li key={label}>
                                    {/* Row labels are data — keep the translation extension away (CALL-759). */}
                                    <Typography variant="body2" component="span" className="notranslate">
                                        {label}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                    </Collapse>
                </Alert>
            )}
            {/* CALL-815: the page's own filter bar. Every control here drives ALL THREE sections below —
                the period, the extrapolation columns, the past months — so it sits above the first heading
                instead of inside it, where the same controls read as belonging to the Ist-Budget alone.
                (What is grid-level stays in its grid's toolbar: "Hide inactive" and "Reload Data". The year
                navigation is not a filter at all — it moves the window — so it sits on the Ist-Budget
                heading row with every other section's nav.)
                Wraps like Prices & ABT's page filter row, so on a narrow laptop the checkboxes drop to a
                second line rather than squeezing the date fields. */}
            <Box
                data-testid="overview-filter-bar"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    // Wider than the column gap on purpose: a date error is painted out of flow under its
                    // field (see BudgetMonthRangeFilter), and ~24px is what keeps that message clear of the
                    // checkboxes once they wrap onto the second line.
                    rowGap: 3,
                    mb: 2,
                }}
            >
                <BudgetMonthRangeFilter />
                {/* CALL-781: NOT inside the !isSnapshot guard below — a published budget is exactly where
                    looking only forward matters, and the AC requires it on snapshots too.
                    Before the extrapolation toggle, because the bar is grouped by CONCERN: the dates and this
                    checkbox are both PERIOD controls — which months are on screen — while "Mit
                    Extrapolation" is a VALUE toggle that changes which figures are computed (and what the
                    export contains), not which months are shown. */}
                <HidePastMonthsFilter checked={hidePastMonths} onChange={setHidePastMonths} />
                {/* CALL-752: extrapolation is a draft-time aid; a snapshot is frozen entered-only,
                    so the toggle doesn't apply — hide it (and its hint) entirely on snapshots. */}
                {!isSnapshot && (
                    // Its own flex box, so the bar's column gap separates the two CONTROLS while the `?`
                    // stays tight against the label it belongs to. As a bare fragment the icon sat
                    // 12px from either neighbour and read as if it explained "Hide past months".
                    // Same wrapper HideInactiveFilter gets one row down.
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={showExtrapolation}
                                    onChange={(e) => setShowExtrapolation(e.target.checked)}
                                    sx={{ py: 0 }}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    <span>{t('summary.withExtrapolation')}</span>
                                </Typography>
                            }
                        />
                        {/* Behind the `?` rather than spelled out beside the checkbox: as a caption it
                            was a second run of grey text competing with the heading. */}
                        <HintIcon notes={t('hint.affectsExport')} />
                    </Box>
                )}
            </Box>
            {/* CALL-815: with "Vergangene Monate ausblenden" ticked on a period that has already passed, the
                filter removes EVERY month column and the three grids below go down to their labels and the
                period total — which is correct (the total covers the whole period, hidden months included)
                but silent. Named here once, at page level: all three grids take their columns from the same
                useOverviewMonths call, so the condition is one condition, not three.
                The filter is the only thing that can empty the list — getWindowMonths always returns 12
                months and an active range always spans at least one — so the message can name it. */}
            {visibleMonths.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <span>{t('hint.allMonthsPast', { filter: t('filter.hidePastMonths') })}</span>
                    </Typography>
                </Alert>
            )}
            {/* The section's own heading row, built like "Handling Data / Costs" one section down: heading on
                the left, its controls pushed right with ml: 'auto'. Every grid on the page carries its window
                navigation here, so the Ist-Budget's belongs here too — in the filter bar it read as a
                page-level filter, which it is not. This is the ONE nav that stays on screen with a range set
                (rangeBehaviour="readout"), so the period is named once, above the first grid. */}
            <Box data-testid="actual-budget-heading" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                    <span>{t('field.actualBudget')}</span>
                </Typography>
                {/* CALL-816: the funded period, next to the row that DEFINES it. Every traffic light on
                    the page is judged over these months (see useBudgetPeriodMonths), so without it a
                    tooltip counting 25 months looks like it is talking about the window on screen. */}
                {periodLabel && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                        <span>{t('summary.budgetPeriod')} </span>
                        <span className="notranslate">{periodLabel}</span>
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <MonthWindowNav rangeBehaviour="readout" />
                </Box>
            </Box>
            <BudgetSummary
                versionId={selectedVersionId}
                onTopCosts={onTopCosts}
                showExtrapolation={showExtrapolation}
                hidePastMonths={hidePastMonths}
                pending={pending}
            />

            <Box sx={{ mt: 3 }} />

            {/* Not space-between: the left group grew past one item, so the right-hand controls are pushed
                over with ml:auto instead of having the gap spread between every child. */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {/* The heading is the switch between the two views of the same table (CALL-783 extracted
                    the pattern; this was its first instance). */}
                <InlineViewToggle<HandlingView>
                    options={[
                        { value: 'values', label: t('field.handlingData') },
                        { value: 'kosten', label: t('field.cost') },
                    ]}
                    value={handlingView}
                    onChange={setHandlingView}
                />
                {/* CALL-778: right after the heading, so it reads like the "Mit Extrapolation" checkbox one
                    row up — same kind of control, same place relative to its own heading. It belongs to
                    THIS toolbar, not the page-level one: it filters only the grid below it. */}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1.5 }}>
                    <HideInactiveFilter checked={hideInactive} onChange={setHideInactive} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    {/* This page's grids DO follow the range (useOverviewMonths), so navigating a window
                        they are not showing makes no sense — but the Ist-Budget heading above already names
                        the period, so this copy gets out of the way instead of repeating it. */}
                    <MonthWindowNav rangeBehaviour="hide" />
                    {!isReadOnly && (
                        <LoadingButton
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={() => refreshSourceData()}
                            loading={isRefreshing}
                            variant="outlined"
                        >
                            <span>{t('action.reloadData')}</span>
                        </LoadingButton>
                    )}
                </Box>
            </Box>
            <ForecastVolumeGrid
                billingConfigs={visibleConfigs}
                isLoading={isBillingConfigsLoading}
                view={handlingView}
                hidePastMonths={hidePastMonths}
                pending={pending}
            />

            <Box sx={{ mt: 3 }} />

            <OnTopCostsGrid
                onTopCosts={onTopCosts}
                isLoading={isOnTopCostsLoading}
                versionId={selectedVersionId}
                hidePastMonths={hidePastMonths}
                pending={pending}
            />

            <UnsavedEditsPrompt isDirty={pending.hasEdits} onCurrentView={pending.isDirty} />

            {!isReadOnly && <OverviewSaveBar versionId={selectedVersionId} pending={pending} />}
        </>
    );
};

export default BudgetOverview;
